import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import { useCallback } from 'react'

import Icon from './svg'

import { useTranslation } from '@/lang'
import { EngagementType } from '@/features/community/api/document/community-document.types'

type UserProfileType = 'document' | 'comment'

export interface UserProfileInCommunityProps {
  type: UserProfileType
  profileImageURL: string
  name: string
  engagementType: EngagementType | null
  detail: {
    doctorId?: number
    level?: number
    createTime?: string
    isEditedTextVisible?: boolean
    isWriterTagVisible?: boolean
  }
  onClickDoctorProfile?: () => void
}

const UserProfileInCommunity = ({
  type,
  profileImageURL,
  name,
  engagementType,
  detail,
  onClickDoctorProfile,
}: UserProfileInCommunityProps): JSX.Element => {
  const { t } = useTranslation()

  const router = useRouter()

  const isAdmin = detail.level !== undefined && detail.level >= 999
  const isDoctor = detail.doctorId !== undefined && !isAdmin

  const onClickProfileImage = useCallback(() => {
    if (isDoctor) {
      onClickDoctorProfile?.()
      router.push(`/doctors/${detail.doctorId}`)
    }
  }, [isDoctor, onClickDoctorProfile, router, detail.doctorId])

  return (
    <StyledContainer $type={type}>
      <StyledProfileImage
        $type={type}
        $isDoctor={isDoctor}
        src={profileImageURL}
        alt="profile image"
        onClick={onClickProfileImage}
      />
      <StyledProfileBody $type={type}>
        <StyledProfileName $type={type}>
          {!isDoctor && <span>{name}</span>}

          {isDoctor && (
            <>
              <Link href={`/doctors/${detail.doctorId}`} onClick={() => onClickDoctorProfile?.()}>
                {name}
              </Link>
              <StyledDot>·</StyledDot>
              <Icon.doctor />
              {type === 'document' && <StyledSubName>{t('pages.communityDetail.userProfile.doctor')}</StyledSubName>}
            </>
          )}

          {isAdmin && (
            <>
              <StyledDot>·</StyledDot>
              <Icon.admin />
              {type === 'document' && <StyledSubName>{t('pages.communityDetail.userProfile.admin')}</StyledSubName>}
            </>
          )}

          {detail.isWriterTagVisible && <StyledSubName>{t('pages.communityDetail.userProfile.writer')}</StyledSubName>}
        </StyledProfileName>

        {!!engagementType && (
          <StyledProfileDescription $engagementType={engagementType}>
            {t(`community.writer.engagement.${engagementType.toLocaleLowerCase()}`)}
            {type === 'comment' && (
              <>
                <StyledDot>·</StyledDot>
                <StyledProfileDate>
                  {detail.createTime}
                  {detail.isEditedTextVisible && t('pages.communityDetail.userProfile.edited')}
                </StyledProfileDate>
              </>
            )}
          </StyledProfileDescription>
        )}

        {type === 'comment' && !engagementType && (
          <StyledProfileDescription>
            {detail.createTime}
            {detail.isEditedTextVisible && `(${t('pages.communityDetail.userProfile.edited')})`}
          </StyledProfileDescription>
        )}
      </StyledProfileBody>
    </StyledContainer>
  )
}

const StyledContainer = styled.div<{ $type: UserProfileType }>`
  display: flex;
  gap: ${({ $type }) => ($type === 'document' ? '12px' : '8px')};
`
const StyledProfileImage = styled.img<{ $type: UserProfileType; $isDoctor: boolean }>`
  border-radius: 100%;
  width: ${({ $type }) => ($type === 'document' ? '48px' : '24px')};
  height: ${({ $type }) => ($type === 'document' ? '48px' : '24px')};
  cursor: ${({ $isDoctor }) => ($isDoctor ? 'pointer' : 'default')};
`
const StyledProfileBody = styled.div<{ $type: UserProfileType }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ $type }) => ($type === 'document' ? '5px' : '4px')};
`

const StyledProfileName = styled.div<{ $type: UserProfileType }>`
  ${({ $type, theme }) => ($type === 'document' ? theme.typography.title3 : theme.typography.title4)};
  color: ${({ theme }) => theme.colors.gray900};
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 5px;

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.gray900};
  }
`
const StyledProfileDescription = styled.p<{ $engagementType?: EngagementType | null }>`
  ${({ theme }) => theme.typography.caption1};
  color: ${({ theme }) => theme.colors.gray800};
  display: flex;
  align-items: center;
  margin: 0px;
  gap: 4px;
  ${({ $engagementType, theme }) =>
    $engagementType === 'OPERATED' &&
    css`
      color: ${theme.colors.latte};
    `}
`
const StyledProfileDate = styled.span`
  ${({ theme }) => theme.typography.caption1};
  color: ${({ theme }) => theme.colors.gray700};
`

const StyledDot = styled.span`
  ${({ theme }) => theme.typography.title4};
  color: ${({ theme }) => theme.colors.gray800};
  line-height: 0;
`

const StyledSubName = styled.span`
  ${({ theme }) => theme.typography.caption1};
  color: ${({ theme }) => theme.colors.gray800};
`

export default UserProfileInCommunity
